import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Search, Link2, X as XIcon, CheckCircle } from 'lucide-react';
import { adminCwMemberAPI } from '../../services/api';

/**
 * Optional portal-user → CW member mapping. When set, the portal posts notes,
 * tickets, and time entries with member: {id} so CW shows that member as the
 * native author. When not set, the portal still tracks authorship internally
 * (via portal_note_authors) so the correct name shows in the portal; in CW the
 * note body just starts with the human's name.
 */
export default function CwMemberMappingCard({ userId }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [savedNotice, setSavedNotice] = useState(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['cw-member-suggestion', userId],
    queryFn: () => adminCwMemberAPI.getSuggestion(userId),
  });

  const setMutation = useMutation({
    mutationFn: (cwMemberId) => adminCwMemberAPI.setMapping(userId, cwMemberId || null),
    onSuccess: (_, cwMemberId) => {
      setSavedNotice(cwMemberId ? 'Linked.' : 'Unlinked.');
      refetch();
      setTimeout(() => setSavedNotice(null), 2500);
    },
  });

  useEffect(() => {
    if (search.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await adminCwMemberAPI.searchMembers(search.trim());
        setResults(resp.data?.items || []);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const linked = data?.data?.linked;
  const suggestion = data?.data?.suggestion;

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
      <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
        <h2 className="text-lg font-medium text-neutral-900 dark:text-white">
          ConnectWise Member
        </h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Optional. Linking lets ConnectWise show this portal user natively as the author on
          tickets, notes, and time entries. Without a link, the portal still shows the correct
          name everywhere by tracking authorship internally; in CW the name appears as the first
          line of the note. If this user has a CW license or a free "API Member" account, linking
          is the cleanest option. Time entries require a link.
        </p>
      </div>
      <div className="p-4 space-y-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : linked ? (
          <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
            <div>
              <div className="flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                <Link2 className="w-4 h-4 text-green-600" />
                {linked.name}
              </div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                {linked.identifier}
                {linked.email && ` • ${linked.email}`}
              </div>
            </div>
            <button
              onClick={() => setMutation.mutate(null)}
              disabled={setMutation.isPending}
              className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
            >
              <XIcon className="w-4 h-4" /> Unlink
            </button>
          </div>
        ) : (
          <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 border border-neutral-200 dark:border-neutral-600 rounded-lg text-sm text-neutral-700 dark:text-neutral-300">
            Not linked. Portal-side authorship tracking will be used for notes.
          </div>
        )}

        {suggestion && (!linked || linked.id !== suggestion.id) && (
          <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div>
              <div className="text-sm font-medium text-neutral-900 dark:text-white">
                Suggested match (by email): {suggestion.name}
              </div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                {suggestion.identifier}
                {suggestion.email && ` • ${suggestion.email}`}
              </div>
            </div>
            <button
              onClick={() => setMutation.mutate(suggestion.id)}
              disabled={setMutation.isPending}
              className="px-3 py-1 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              Link this member
            </button>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
            Search ConnectWise members
          </label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, identifier, or email…"
              className="w-full pl-8 pr-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
            />
            {searching && (
              <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-neutral-400" />
            )}
          </div>
          {results.length > 0 && (
            <div className="mt-2 border border-neutral-200 dark:border-neutral-700 rounded-lg max-h-48 overflow-y-auto">
              {results.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    setMutation.mutate(m.id);
                    setSearch('');
                    setResults([]);
                  }}
                  className="w-full flex items-center justify-between p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 text-left"
                >
                  <div>
                    <div className="text-sm font-medium text-neutral-900 dark:text-white">{m.name}</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                      {m.identifier}
                      {m.email && ` • ${m.email}`}
                    </div>
                  </div>
                  <span className="text-xs text-primary-600">Link</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {savedNotice && (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <CheckCircle className="w-4 h-4" /> {savedNotice}
          </div>
        )}
      </div>
    </div>
  );
}
